import { buildproVertical } from '../../verticals/buildpro/descriptor';
import type {
  VerticalDescriptor,
  VerticalMenuItem,
  VerticalRoute,
  VerticalEngineEvent,
} from './types';

const _registry = new Map<string, VerticalDescriptor>();
const _eventListeners = new Set<(e: VerticalEngineEvent) => void>();

export function registerVertical(descriptor: VerticalDescriptor): void {
  if (!descriptor.id || !descriptor.name) {
    console.error('[vertical-engine] registerVertical: missing required fields (id, name)');
    return;
  }
  const isUpdate = _registry.has(descriptor.id);
  _registry.set(descriptor.id, descriptor);
  _emit({ type: 'vertical:registered', id: descriptor.id });
  if (isUpdate) {
    console.log(`[vertical-engine] ↺ Updated vertical: ${descriptor.id} v${descriptor.version}`);
  } else {
    console.log(`[vertical-engine] ✓ Registered vertical: ${descriptor.id} v${descriptor.version}`);
  }
}

export async function loadVerticalModules(): Promise<VerticalDescriptor[]> {
  const loaded: VerticalDescriptor[] = [buildproVertical];

  for (const [id, v] of _registry) {
    if (v.init) {
      try {
        await v.init();
        _emit({ type: 'vertical:initialized', id });
        console.log(`[vertical-engine] ✓ Initialized: ${id}`);
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        _emit({ type: 'vertical:error', id, error });
        console.error(`[vertical-engine] ✗ Init failed for ${id}:`, error);
        continue;
      }
    }
    loaded.push(v);
  }

  return loaded;
}

export function extendMenu(): VerticalMenuItem[] {
  const items: VerticalMenuItem[] = [...(buildproVertical.menu ?? [])];
  for (const v of _registry.values()) {
    if (v.menu) items.push(...v.menu);
  }
  return items.sort((a, b) => {
    const orderDiff = (a.order ?? 999) - (b.order ?? 999);
    return orderDiff !== 0 ? orderDiff : a.label.localeCompare(b.label);
  });
}

export function extendRoutes(): VerticalRoute[] {
  const routes: VerticalRoute[] = [...(buildproVertical.routes ?? [])];
  for (const v of _registry.values()) {
    if (v.routes) routes.push(...v.routes);
  }
  return routes;
}

export function getRegisteredVerticals(): VerticalDescriptor[] {
  return [buildproVertical, ...Array.from(_registry.values())];
}

export function isVerticalRegistered(id: string): boolean {
  return id === 'buildpro' || _registry.has(id);
}

export function onVerticalEvent(handler: (e: VerticalEngineEvent) => void): () => void {
  _eventListeners.add(handler);
  return () => _eventListeners.delete(handler);
}

function _emit(event: VerticalEngineEvent): void {
  _eventListeners.forEach(fn => {
    try { fn(event); } catch { }
  });
}

export type { VerticalDescriptor, VerticalMenuItem, VerticalRoute, VerticalEngineEvent };
